import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/use-toast'
import { EditContentDialogProps } from '../../utils/contentTypes'
import { editableStatuses, getStatusLabel } from '../../utils/contentUtils'
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"

// Schema definition for form validation
// Note: Only including statuses that can be set directly by users
// (PROCESSING, FAILED, HOLD, HELD, CANCELLED are computed/system statuses)
const editContentSchema = z.object({
    name: z.string().min(1, "Name is required"),
    status: z.enum(['GENERATING', 'READY', 'SCHEDULED', 'DRAFT', 'PUBLISHED', 'ERROR', 'ARCHIVED', 'DELETED'] as const)
})

/**
 * Dialog for editing content metadata (name, status)
 */
export function EditContentDialog({
    content,
    isOpen,
    onClose,
    onSave
}: EditContentDialogProps) {
    const form = useForm<z.infer<typeof editContentSchema>>({
        resolver: zodResolver(editContentSchema),
        defaultValues: {
            name: content.name,
            // Use content.status if it's editable, otherwise default to READY
            status: editableStatuses.includes(content.status as any)
                ? (content.status as z.infer<typeof editContentSchema>['status'])
                : 'READY'
        }
    })

    const onSubmit = async (values: z.infer<typeof editContentSchema>) => {
        try {
            await onSave(values)
            onClose()
        } catch (error) {
            console.error('Failed to update content:', error)
            toast({
                title: "Error",
                description: "Failed to update content. Please try again.",
                variant: "destructive"
            })
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit Content</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {editableStatuses.map(status => (
                                                <SelectItem key={status} value={status}>
                                                    {getStatusLabel(status)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="submit">Save Changes</Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}